import React, { useState, useEffect, useCallback } from 'react';
import { FiCheckCircle, FiX, FiAlertTriangle } from 'react-icons/fi';
import QRScanner from './QRScanner';
import API from 'shared/api';
import { queueAttendance, flushQueue, getQueueCount } from 'shared/attendanceQueue';
import { playNotificationSound } from 'shared/notificationSound';

const S = {
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modalContent: {
    background: 'var(--agx-surface)',
    borderRadius: '20px',
    padding: '28px',
    width: '100%',
    maxWidth: '440px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--agx-text)',
    margin: '0 0 8px',
    textAlign: 'center',
    letterSpacing: '-0.2px',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--agx-muted)',
    textAlign: 'center',
    marginBottom: '20px',
    lineHeight: 1.5,
  },
  btn: {
    flex: 1,
    padding: '16px 20px',
    borderRadius: '16px',
    border: 'none',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'all 0.15s',
  },
  confirmBox: {
    background: '#f0fdf4',
    borderRadius: '16px',
    padding: '28px',
    textAlign: 'center',
    border: '1px solid #bbf7d0',
  },
  errorBox: {
    background: '#fef2f2',
    borderRadius: '16px',
    padding: '28px',
    textAlign: 'center',
    border: '1px solid #fecaca',
  },
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8',
    padding: '6px',
    borderRadius: '8px',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1.5px solid #d1d5db',
    fontSize: '13px',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '64px',
    boxSizing: 'border-box',
    background: '#fafbfd',
    transition: 'border-color 0.15s',
  },
  actionBtn: (bg, color) => ({
    padding: '10px 22px',
    borderRadius: '12px',
    border: 'none',
    background: bg,
    color: color,
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  }),
};

export default function AsistenciaModal({ open, onClose, onSuccess, miEstado }) {
  const [step, setStep] = useState('select');
  const [tipo, setTipo] = useState(null);
  const [registrado, setRegistrado] = useState(null);
  const [error, setError] = useState(null);
  const [notas, setNotas] = useState('');
  const [registroId, setRegistroId] = useState(null);

  const reset = useCallback(() => {
    setStep('select');
    setTipo(null);
    setRegistrado(null);
    setError(null);
    setNotas('');
    setRegistroId(null);
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleSelectType = (t) => {
    setTipo(t);
    setStep('scanning');
  };

  const handleScan = async (tokenQR, notasEnvio) => {
    let token = tokenQR;
    try {
      const p = JSON.parse(tokenQR);
      token = p.token || tokenQR;
    } catch (e) {
      if (tokenQR.startsWith('http')) {
        const u = new URL(tokenQR);
        const t = u.searchParams.get('token');
        if (t) token = t;
      }
    }

    const n = new Date();
    const p = (v) => String(v).padStart(2, '0');
    const hora_cliente =
      n.getFullYear() +
      '-' +
      p(n.getMonth() + 1) +
      '-' +
      p(n.getDate()) +
      'T' +
      p(n.getHours()) +
      ':' +
      p(n.getMinutes()) +
      ':' +
      p(n.getSeconds());

    let envioOk = false;
    let envioError = null;
    let registroData = null;
    try {
      const body = { tipo: tipo, token_qr: token, hora_cliente };
      if (notasEnvio) body.notas = notasEnvio;
      const res = await API.post('/asistencia/registrar', body);
      envioOk = true;
      registroData = res.data?.data;
    } catch (e) {
      const s = e.response?.status;
      const d = e.response?.data?.detail;
      if (s === 422) {
        try {
          const params = { tipo, token_qr: token, hora_cliente };
          if (notasEnvio) params.notas = notasEnvio;
          const res2 = await API.post('/asistencia/registrar', null, { params });
          envioOk = true;
          registroData = res2.data?.data;
        } catch (e2) {
          envioError = d || 'Error de validacion del servidor.';
        }
      } else if (s === 409) {
        envioOk = true;
        envioError = null;
        registroData = null;
      } else if (s === 400) {
        envioError = d || 'QR no valido, expirado o revocado.';
      } else if (s) {
        envioError = d || 'Error del servidor. Se intentara en segundo plano.';
      } else {
        envioError = 'Error de conexión. Se intentará en segundo plano.';
      }
    }

    if (envioOk) {
      // Reutiliza el AudioContext singleton de notificationSound (no crea uno nuevo
      // por escaneo, evitando la fuga de AudioContext).
      playNotificationSound('normal');
      setRegistrado({
        tipo,
        hora: new Date().toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' }),
      });
      setStep('done');
      if (registroData?.id) setRegistroId(registroData.id);
      flushQueue(API);
      if (onSuccess) onSuccess();
    } else {
      queueAttendance(tipo, token);
      setError(envioError);
      setStep('error');
      flushQueue(API);
    }
  };

  const handleScanWithNotas = async () => {
    if (!registroId || !notas.trim()) return;
    try {
      await API.patch('/asistencia/registros/' + registroId + '/observacion', null, {
        params: { observacion: notas.trim() },
      });
      setNotas('');
      setRegistrado((prev) => (prev ? { ...prev, notasEnviada: true } : prev));
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al enviar observacion');
      setStep('error');
    }
  };

  if (!open) return null;

  const activo = miEstado?.activo;
  const queueCount = getQueueCount();

  return (
    <div
      style={S.modal}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div style={S.modalContent}>
        <button style={S.closeBtn} onClick={handleClose}>
          <FiX size={20} />
        </button>

        {step === 'select' && (
          <>
            <div style={S.title}>Reportar asistencia</div>
            <div style={S.subtitle}>
              {activo
                ? 'Estas marcado como activo. Selecciona salida si vas a retirarte.'
                : 'No estas activo. Selecciona entrada para comenzar tu jornada.'}
            </div>
            <div
              style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '8px' }}
            >
              {!activo && (
                <button
                  onClick={() => handleSelectType('entrada')}
                  style={{ ...S.btn, background: '#16a34a', color: '#fff' }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = '')}
                >
                  <FiCheckCircle size={20} /> Entrando
                </button>
              )}
              {activo && (
                <button
                  onClick={() => handleSelectType('salida')}
                  style={{ ...S.btn, background: '#dc2626', color: '#fff' }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = '')}
                >
                  <FiX size={20} /> Saliendo
                </button>
              )}
            </div>
          </>
        )}

        {step === 'scanning' && (
          <>
            <div
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: 'var(--agx-text)',
                textAlign: 'center',
                marginBottom: '12px',
              }}
            >
              Escanea el QR {tipo === 'entrada' ? 'de entrada' : 'de salida'}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: '#9ca3af',
                textAlign: 'center',
                marginBottom: '16px',
              }}
            >
              Apunta la cámara al código QR de la sede
            </div>
            <QRScanner
              onScan={handleScan}
              onCancel={() => {
                setStep('select');
              }}
              facingMode="environment"
            />
          </>
        )}

        {step === 'error' && error && (
          <div style={S.errorBox}>
            <FiAlertTriangle size={48} color="#dc2626" style={{ marginBottom: '16px' }} />
            <div
              style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626', marginBottom: '8px' }}
            >
              No se pudo registrar
            </div>
            <div
              style={{
                fontSize: '14px',
                color: 'var(--agx-muted)',
                marginBottom: '8px',
                lineHeight: 1.6,
              }}
            >
              {error}
            </div>
            {queueCount > 0 && (
              <div
                style={{
                  fontSize: '12px',
                  color: '#f59e0b',
                  marginBottom: '16px',
                  background: '#fef9c3',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  display: 'inline-block',
                }}
              >
                {queueCount} pendiente(s) en cola de reintento
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  setStep('scanning');
                  setError(null);
                }}
                style={S.actionBtn('#1a73e8', '#fff')}
              >
                Reintentar
              </button>
              <button
                onClick={handleClose}
                style={{
                  ...S.closeBtn,
                  position: 'static',
                  padding: '10px 22px',
                  borderRadius: '12px',
                  border: '1.5px solid #d1d5db',
                  background: 'var(--agx-surface)',
                  color: 'var(--agx-muted)',
                  fontSize: '14px',
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        )}

        {step === 'done' && registrado && (
          <div style={S.confirmBox}>
            <FiCheckCircle size={56} color="#16a34a" style={{ marginBottom: '14px' }} />
            <div
              style={{ fontSize: '20px', fontWeight: 700, color: '#16a34a', marginBottom: '4px' }}
            >
              {registrado.tipo === 'entrada' ? 'Entrada registrada' : 'Salida registrada'}
            </div>
            <div
              style={{
                fontSize: '32px',
                fontWeight: 700,
                color: 'var(--agx-text)',
                marginBottom: '6px',
                letterSpacing: '-0.5px',
              }}
            >
              {registrado.hora}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--agx-muted)', marginBottom: '20px' }}>
              <FiCheckCircle
                size={12}
                style={{ verticalAlign: 'middle', marginRight: '4px', color: '#16a34a' }}
              />
              Registro confirmado en el servidor
            </div>

            <div
              style={{
                textAlign: 'left',
                background: 'var(--agx-surface)',
                borderRadius: '12px',
                padding: '14px',
                border: '1px solid #eef0f6',
              }}
            >
              <textarea
                placeholder="Observacion opcional (ej: motivo de salida, notas del dia...)"
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                style={S.textarea}
                rows={3}
                disabled={registrado?.notasEnviada}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#1a73e8')}
                onBlur={(e) => (e.currentTarget.style.borderColor = '#d1d5db')}
              />
              <button
                onClick={handleScanWithNotas}
                disabled={!notas.trim() || registrado?.notasEnviada}
                style={{
                  marginTop: '10px',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: 'none',
                  background: registrado?.notasEnviada ? '#16a34a' : '#1a73e8',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: registrado?.notasEnviada ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  width: '100%',
                }}
              >
                {registrado?.notasEnviada ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <FiCheckCircle size={14} /> Observacion enviada
                  </span>
                ) : (
                  'Enviar observacion'
                )}
              </button>
            </div>

            {queueCount > 0 && (
              <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '12px' }}>
                {queueCount} pendiente(s) en cola de reintento
              </div>
            )}
            <button
              onClick={handleClose}
              style={{
                marginTop: '20px',
                padding: '12px 32px',
                borderRadius: '14px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
            >
              Listo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
